import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Bot, Loader2, X, MessageCircle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { buildAuthRedirectToUrl } from '@om/shared-auth';
import { aiService } from '@/lib/api/ai.service';
import { streamChatService } from '@/lib/api/streamChat.service';
import { UserService } from '@/lib/api/user.service';
import { useAuth } from '@/hooks/useAuth';
import { useConversation } from '@/contexts/ConversationContext';
import { useToast } from '@/hooks/use-toast';
import { useChatAutocomplete } from '@/components/celebration-chat/hooks/useChatAutocomplete';
import { AutocompleteDropdown } from '@/components/celebration-chat/AutocompleteDropdown';
import type { AutocompleteItem } from '@/components/celebration-chat/hooks/useChatAutocomplete';
import { groupService } from '@/lib/api/group.service';
import type { GuestGroup } from '@/types/group.types';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  isError?: boolean;
  originalMessage?: string; // For retry functionality
}

interface ConversationSidebarProps {
  onEventCreated?: (eventData: unknown) => void;
  onClose?: () => void;
  pageContext?: {
    currentPage?: string;
    pageType?: string;
    celebrationId?: number;
    celebrationTitle?: string;
    celebrationType?: string;
    celebrationDescription?: string;
    eventId?: number;
    eventName?: string;
    eventType?: string;
    eventDescription?: string;
    eventDate?: string;
  };
}

interface ExtractedData {
  celebrationId?: number;
  eventId?: number | string;
  celebrationType?: string;
  eventType?: string;
  [key: string]: unknown;
}

interface ApiErrorShape {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
  code?: string;
  message?: string;
}

// Regex to parse /message @Name optional-text
// @"Multi Word Name" for multi-word names, or @SingleName for single-word names
const MESSAGE_CMD_QUOTED = /^\/message\s+@"([^"]+)"\s*(.*)?$/i;
const MESSAGE_CMD_SIMPLE = /^\/message\s+@(\S+)\s*(.*)?$/i;

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  onEventCreated,
  onClose,
  pageContext = {}
}) => {
  const { t } = useTranslation('common');
  const { user, isAuthenticated } = useAuth();
  const { pendingCommand, consumePendingCommand } = useConversation();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const loginHref = typeof window === 'undefined'
    ? '/auth'
    : buildAuthRedirectToUrl(window.location.href);
  
  // Generate context-aware initial message
  const getInitialMessage = useCallback(() => {
    const { pageType, celebrationTitle, eventName } = pageContext;
    const userName = user?.full_name?.split(' ')[0] || t('conversation.sidebar.fallbackUserName');
    
    switch (pageType) {
      case 'celebration-details':
        return t('conversation.sidebar.initial.celebrationDetails', { userName, celebrationTitle });

      case 'celebration-events':
        return t('conversation.sidebar.initial.celebrationEvents', { userName, celebrationTitle });

      case 'celebration-tasks':
        return t('conversation.sidebar.initial.celebrationTasks', { userName, celebrationTitle });

      case 'celebration-guests':
        return t('conversation.sidebar.initial.celebrationGuests', { userName, celebrationTitle });

      case 'celebration-services':
        return t('conversation.sidebar.initial.celebrationServices', { userName, celebrationTitle });

      case 'event':
        return t('conversation.sidebar.initial.event', { userName, eventName });

      case 'create-celebration':
        return t('conversation.sidebar.initial.createCelebration', { userName });

      case 'create-event':
        return t('conversation.sidebar.initial.createEvent', {
          userName,
          celebrationSuffix: celebrationTitle ? t('conversation.sidebar.initial.createEventFor', { celebrationTitle }) : '',
        });

      case 'all-events':
        return t('conversation.sidebar.initial.allEvents', { userName });

      default:
        return t('conversation.sidebar.initial.default', { userName });
    }
  }, [pageContext, t, user?.full_name]);

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: getInitialMessage(),
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData>({});
  const [conversationId, setConversationId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [groups, setGroups] = useState<GuestGroup[]>([]);

  // Fetch groups when celebrationId is available
  useEffect(() => {
    const celebrationId = pageContext.celebrationId;
    if (!celebrationId) {
      setGroups([]);
      return;
    }
    groupService.getGroups(celebrationId)
      .then(setGroups)
      .catch(() => setGroups([]));
  }, [pageContext.celebrationId]);

  // Autocomplete hook
  const {
    state: autocompleteState,
    handleInputChange: handleAutocompleteChange,
    moveSelection,
    getSelectedItem,
    applySelection,
    close: closeAutocomplete,
  } = useChatAutocomplete({ groups });

  const handleAutocompletePick = useCallback(
    (item: AutocompleteItem) => {
      const el = inputRef.current;
      if (!el) return;
      const cursorPos = el.selectionStart ?? inputText.length;
      const { newText, newCursorPos } = applySelection(inputText, cursorPos, item);
      setInputText(newText);
      closeAutocomplete();
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(newCursorPos, newCursorPos);
      });
    },
    [inputText, applySelection, closeAutocomplete],
  );

  // Pick up pending command from context (e.g. from "Message" button clicks)
  useEffect(() => {
    if (pendingCommand) {
      const cmd = consumePendingCommand();
      if (cmd) {
        setInputText(cmd);
        // Focus the input so user can immediately start typing the message body
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  }, [pendingCommand, consumePendingCommand]);

  // Reset conversation when context changes significantly (different celebration/event)
  useEffect(() => {
    const currentCelebrationId = pageContext.celebrationId;
    const currentEventId = pageContext.eventId;
    const currentPageType = pageContext.pageType;
    
    // Reset if we switch to a different celebration or event
    const shouldReset = (
      (currentCelebrationId && currentCelebrationId !== extractedData.celebrationId) ||
      (currentEventId && currentEventId !== extractedData.eventId) ||
      (currentPageType === 'create-celebration' || currentPageType === 'create-event')
    );

    if (shouldReset) {
      setMessages([{
        id: Date.now().toString(),
        text: getInitialMessage(),
        isUser: false,
        timestamp: new Date()
      }]);
      setExtractedData({
        celebrationId: currentCelebrationId,
        eventId: currentEventId,
        celebrationType: pageContext.celebrationType,
        eventType: pageContext.eventType
      });
      setConversationId(''); // Reset conversation for new context
    }
  }, [
    extractedData.celebrationId,
    extractedData.eventId,
    getInitialMessage,
    pageContext.celebrationId,
    pageContext.celebrationType,
    pageContext.eventId,
    pageContext.eventType,
    pageContext.pageType,
  ]);

  // Handle MCP conversation with intent classification
  const handleMCPConversation = async (message: string) => {
    try {
      const response = await aiService.processConversation({
        message,
        conversationId,
        context: {
          // Page context
          currentPage: pageContext.currentPage,
          pageType: pageContext.pageType,
          
          // Celebration context
          celebrationId: pageContext.celebrationId || extractedData.celebrationId,
          celebrationTitle: pageContext.celebrationTitle,
          celebrationType: pageContext.celebrationType || extractedData.celebrationType,
          celebrationDescription: pageContext.celebrationDescription,
          
          // Event context
          eventId: pageContext.eventId?.toString() || (extractedData.eventId != null ? String(extractedData.eventId) : undefined),
          eventName: pageContext.eventName,
          eventType: pageContext.eventType || extractedData.eventType,
          eventDescription: pageContext.eventDescription,
          eventDate: pageContext.eventDate,
          
          // Previous actions for continuity
          previousActions: messages.map(m => m.text).slice(-3)
        }
      });

      // Update conversation state
      if (response.conversationId) {
        setConversationId(response.conversationId);
      }

      // Update extracted data with any new fields
      if (response.extractedFields) {
        setExtractedData((prev) => ({ ...prev, ...response.extractedFields }));
      }

      // Handle successful event creation
      if (response.action === 'complete' && response.eventId && onEventCreated) {
        onEventCreated({
          ...response.extractedFields,
          eventId: response.eventId
        });
      }

      return response;
    } catch (error: unknown) {
      // Re-throw with enhanced error information
      const enhancedError = {
        ...(typeof error === 'object' && error !== null ? error : {}),
        mcpContext: 'MCP orchestrator communication failed',
        userMessage: message,
        pageContext: pageContext
      };
      
      throw enhancedError;
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleRetryMessage = async (originalMessage: string, errorMessageId: string) => {
    // Remove the error message
    setMessages(prev => prev.filter(m => m.id !== errorMessageId));
    
    // Retry the original message
    setIsLoading(true);
    
    try {
      const response = await handleMCPConversation(originalMessage);
      
      const aiMessage: Message = {
        id: Date.now().toString(),
        text: Array.isArray(response.response) ? response.response.join(' ') : response.response,
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
      
    } catch (error: unknown) {
      const apiError = error as ApiErrorShape;
      console.error('Retry failed:', error);
      
      // Show error again with updated message
      let errorText = "Retry failed. Please check your connection and try again.";
      if (apiError?.response?.status === 401) {
        errorText = "🔒 Authentication error. Please try logging in again.";
      } else if (apiError?.response?.data?.message) {
        errorText = `❌ ${apiError.response.data.message}`;
      }
      
      const errorMessage: Message = {
        id: Date.now().toString(),
        text: errorText,
        isUser: false,
        timestamp: new Date(),
        isError: true,
        originalMessage: originalMessage
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle the /message slash command.
   * Syntax: /message @RecipientName optional message text
   * Searches for the user by name and opens a Stream Chat DM channel.
   */
  const handleMessageCommand = async (recipientName: string, messageBody?: string) => {
    void messageBody;
    try {
      // Search for user by name using app user service (primary) with Stream Chat fallback
      let matchedUserId: number | null = null;
      let matchedUserName = recipientName;

      // Try app user service first (searches full_name, email)
      try {
        const allUsers = await UserService.getAllUsers();
        const nameLower = recipientName.trim().toLowerCase();
        const match = allUsers.find(u =>
          u.full_name?.toLowerCase().includes(nameLower) ||
          u.email?.toLowerCase().includes(nameLower)
        );
        if (match) {
          matchedUserId = match.id ?? match.user_id;
          matchedUserName = match.full_name || recipientName;
        }
      } catch {
        // App user search failed, try Stream Chat search
      }

      // Fallback to Stream Chat user search
      if (!matchedUserId) {
        try {
          const streamUsers = await streamChatService.searchUsers(recipientName.trim(), 5);
          if (streamUsers && streamUsers.length > 0) {
            const su = streamUsers[0];
            matchedUserId = typeof su.id === 'string' ? parseInt(su.id, 10) : su.id;
            matchedUserName = su.name || su.full_name || recipientName;
          }
        } catch {
          // Stream Chat search also failed
        }
      }

      if (!matchedUserId) {
        const notFoundMsg: Message = {
          id: (Date.now() + 1).toString(),
          text: t('conversation.sidebar.messages.userNotFound', { recipientName }),
          isUser: false,
          timestamp: new Date(),
          isError: true,
        };
        setMessages(prev => [...prev, notFoundMsg]);
        return;
      }

      // Create/open the DM channel
      await streamChatService.startDirectConversation({
        otherUserId: matchedUserId,
        celebrationId: pageContext.celebrationId || extractedData.celebrationId,
      });

      const successMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: t('conversation.sidebar.messages.conversationReady', { matchedUserName }),
        isUser: false,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, successMessage]);

      toast({
        title: t('conversation.sidebar.toast.conversationReadyTitle'),
        description: t('conversation.sidebar.toast.conversationReadyDescription', { matchedUserName }),
      });

    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : t('conversation.sidebar.errors.startConversationFailed');
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: `❌ ${errorMsg}`,
        isUser: false,
        timestamp: new Date(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);

      toast({
        title: t('conversation.sidebar.toast.errorTitle'),
        description: errorMsg,
        variant: 'destructive',
      });
    }
  };

  /**
   * Parse and execute slash commands.
   * Returns true if the input was a recognized command, false otherwise.
   */
  const tryHandleSlashCommand = async (input: string): Promise<boolean> => {
    const trimmed = input.trim();

    // /message @"Multi Word Name" optional text
    const quotedMatch = trimmed.match(MESSAGE_CMD_QUOTED);
    if (quotedMatch) {
      const recipientName = quotedMatch[1].trim();
      const messageBody = (quotedMatch[2] || '').trim();
      await handleMessageCommand(recipientName, messageBody);
      return true;
    }

    // /message @Name optional text  (first word after @ is the name)
    const simpleMatch = trimmed.match(MESSAGE_CMD_SIMPLE);
    if (simpleMatch) {
      const recipientName = simpleMatch[1].trim();
      const messageBody = (simpleMatch[2] || '').trim();
      await handleMessageCommand(recipientName, messageBody);
      return true;
    }

    const generic = trimmed.match(/^\/(\w+)\s*(.*)$/s);
    if (!generic) return false;
    const name = generic[1].toLowerCase();
    const args = generic[2].trim();
    const addBotMessage = (text: string) =>
      setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), text, isUser: false, timestamp: new Date() }]);

    if (name === 'help') {
      addBotMessage(
        'Available commands:\n' +
        '/message @Name - start a direct message\n' +
        '/create - create a new celebration\n/templates - browse celebration templates\n' +
        '/budget - set or update budget\n/invite - invite guests\n/status - check celebration status\n' +
        '/tasks - view or create tasks\n/vendors - search vendors & services\n/reset - start over'
      );
      return true;
    }

    if (name === 'reset') {
      setMessages([{ id: Date.now().toString(), text: getInitialMessage(), isUser: false, timestamp: new Date() }]);
      setExtractedData({
        celebrationId: pageContext.celebrationId,
        eventId: pageContext.eventId,
        celebrationType: pageContext.celebrationType,
        eventType: pageContext.eventType,
      });
      setConversationId('');
      return true;
    }

    const prompts: Record<string, string> = {
      create: 'I want to create a new celebration',
      templates: 'Show me the available celebration templates',
      budget: 'I want to set or update the budget',
      invite: 'I want to invite guests',
      status: 'What is the current status of this celebration?',
      tasks: 'Show me the tasks',
      vendors: 'Help me find vendors and services',
    };
    const prompt = prompts[name];
    if (!prompt) return false;

    const response = await handleMCPConversation(args ? `${prompt}: ${args}` : prompt);
    addBotMessage(Array.isArray(response.response) ? response.response.join(' ') : response.response);
    return true;
  };

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputText,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputText;
    setInputText('');
    setIsLoading(true);

    try {
      // Check for slash commands first
      const wasCommand = await tryHandleSlashCommand(currentInput);
      if (wasCommand) return;

      // Use the new MCP conversation handler
      const response = await handleMCPConversation(currentInput);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: Array.isArray(response.response) ? response.response.join(' ') : response.response,
        isUser: false,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);

    } catch (error: unknown) {
      const apiError = error as ApiErrorShape;
      
      // Create a user-friendly error message based on the error type
      let errorText = t('conversation.sidebar.errors.generic');
      
      if (apiError?.response?.status === 401) {
        errorText = t('conversation.sidebar.errors.auth');
      } else if (apiError?.response?.status === 403) {
        errorText = t('conversation.sidebar.errors.accessDenied');
      } else if (apiError?.response?.status === 404) {
        errorText = t('conversation.sidebar.errors.notFound');
      } else if (apiError?.response?.status === 408) {
        errorText = t('conversation.sidebar.errors.requestTimeout');
      } else if (apiError?.response?.status === 429) {
        errorText = t('conversation.sidebar.errors.tooManyRequests');
      } else if ((apiError?.response?.status ?? 0) >= 500) {
        errorText = t('conversation.sidebar.errors.server');
      } else if (apiError?.code === 'NETWORK_ERROR' || apiError?.code === 'ERR_NETWORK' || apiError?.message?.includes('Network Error')) {
        errorText = t('conversation.sidebar.errors.network');
      } else if (apiError?.code === 'ECONNABORTED' || apiError?.message?.includes('timeout')) {
        errorText = t('conversation.sidebar.errors.connectionTimeout');
      } else if (apiError?.response?.data?.message) {
        // Use specific error message from the backend if available
        errorText = t('conversation.sidebar.errors.backend', { message: apiError.response.data.message });
      } else if (apiError?.message) {
        // Use general error message if available
        errorText = t('conversation.sidebar.errors.message', { message: apiError.message });
      }
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: errorText,
        isUser: false,
        timestamp: new Date(),
        isError: true,
        originalMessage: currentInput
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    // Autocomplete navigation
    if (autocompleteState.isOpen && autocompleteState.items.length > 0) {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveSelection('up');
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveSelection('down');
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const selected = getSelectedItem();
        if (selected) {
          e.preventDefault();
          handleAutocompletePick(selected);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeAutocomplete();
        return;
      }
    }

    // Default Enter sends message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputText(val);
    const cursorPos = e.target.selectionStart ?? val.length;
    handleAutocompleteChange(val, cursorPos);
  };

  return (
    <div className="h-full bg-white border-r border-gray-200 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-purple-700 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MessageCircle className="h-5 w-5 text-white" />
            <h2 className="font-semibold text-white">{t('conversation.sidebar.title')}</h2>
          </div>
          <div className="flex items-center gap-1">
            {onClose && (
              <button
                onClick={onClose}
                className="p-1 hover:bg-white/20 rounded-md transition-colors"
                title={t('conversation.sidebar.actions.close')}
              >
                <X className="h-4 w-4 text-white" />
              </button>
            )}
          </div>
        </div>
        <p className="text-sm text-purple-100 mt-1 font-medium truncate">
          {pageContext.celebrationTitle 
            ? t('conversation.sidebar.context.planning', { title: pageContext.celebrationTitle })
            : pageContext.eventName
              ? t('conversation.sidebar.context.event', { name: pageContext.eventName })
              : t('conversation.sidebar.context.ready')}
        </p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-lg px-3 py-2 ${
                message.isUser
                  ? 'bg-purple-600 text-white'
                  : message.isError
                    ? 'bg-red-50 text-red-900 border border-red-200'
                    : 'bg-gray-100 text-gray-900'
              }`}
            >
              <div className="flex items-start space-x-2">
                {!message.isUser && (
                  <Bot className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                    message.isError ? 'text-red-500' : 'text-purple-600'
                  }`} />
                )}
                <div className="flex-1">
                  <p className="text-sm leading-relaxed">{message.text}</p>
                  {message.isError && message.originalMessage && (
                    <button
                      onClick={() => handleRetryMessage(message.originalMessage!, message.id)}
                      disabled={isLoading}
                      className="inline-flex items-center gap-1 mt-2 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isLoading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3 w-3" />
                      )}
                      {isLoading ? t('conversation.sidebar.actions.retrying') : t('conversation.sidebar.actions.tryAgain')}
                    </button>
                  )}
                  <p className={`text-xs mt-1 ${
                    message.isUser ? 'text-purple-100' : 'text-gray-500'
                  }`}>
                    {message.timestamp.toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-900 rounded-lg px-3 py-2">
              <div className="flex items-center space-x-2">
                <Bot className="h-4 w-4 text-purple-600" />
                <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                <span className="text-sm">{t('conversation.sidebar.thinking')}</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      {isAuthenticated ? (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex space-x-2 relative">
            {/* Autocomplete dropdown */}
            <AutocompleteDropdown
              state={autocompleteState}
              onSelect={handleAutocompletePick}
              onClose={closeAutocomplete}
            />

            <input
              ref={inputRef}
              value={inputText}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              placeholder={t('conversation.sidebar.inputPlaceholder')}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              disabled={isLoading}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputText.trim() || isLoading}
              className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {t('conversation.sidebar.inputHint')}
          </p>
        </div>
      ) : (
        <div className="p-4 border-t border-gray-200 bg-gray-50 text-center text-sm text-gray-500">
          <a
            href={loginHref}
            className="text-purple-600 font-medium hover:underline"
          >
            {t('conversation.sidebar.actions.logIn')}
          </a>{' '}
          {t('conversation.sidebar.loginPromptSuffix')}
        </div>
      )}
    </div>
  );
};
